import { Agent } from '../../types';
import { ClientConfig } from '../../types/config';
import { sanitizeHTML } from '../../utils';

/**
 * Generates a CRM link for a lead source ID
 */
export const getCrmLink = (
  clientConfig: ClientConfig | null,
  leadSourceId?: string,
): string => {
  if (!clientConfig?.crm_link || !leadSourceId) {
    return ''; // Return empty string instead of null
  }
  return clientConfig.crm_link.replace('{lead_source_id}', leadSourceId);
};

/**
 * Formats a date string from UTC to the agent's timezone
 */
export const formatDateFromUTCtoAgent = (
  dateString: string,
  timezone: string,
  options?: { includeZone?: boolean },
): string => {
  // Check if the string already has a timezone indicator (Z or +/- offset)
  const hasTimezone = /(?:[Zz]|[+-]\d{2}(:?\d{2})?)$/.test(dateString);
  const parsedDateString = hasTimezone ? dateString : `${dateString}Z`;

  // Create a date object that explicitly interprets the input as UTC
  const utcDate = new Date(parsedDateString);

  // Format the date in the agent's timezone or fallback
  const formatted = utcDate.toLocaleString('en', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  if (!options?.includeZone) {
    return formatted;
  }

  // Intl rejects timeZoneName alongside dateStyle/timeStyle, so resolve the short
  // abbreviation (e.g. "MDT") separately and append it.
  const zone = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(utcDate)
    .find(part => part.type === 'timeZoneName')?.value;

  return zone ? `${formatted} ${zone}` : formatted;
};

/**
 * Formats a date string in the agent's timezone
 */
export const formatDate = (dateString: string, timezone: string): string => {
  if (!dateString) {
    return 'No date set';
  }

  // Parse the date string
  const date = new Date(dateString);

  // Format the date in the agent's timezone
  return date.toLocaleString('en', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

/**
 * Formats a date string in the agent's timezone, with option to hide time if midnight
 */
export const formatDateWithOptionalTime = (
  dateString: string | null | undefined,
  timezone: string,
): string => {
  if (!dateString) {
    return 'No date set';
  }

  // Parse the date string
  const date = new Date(dateString);

  // Check if it's midnight GMT (common for date-only fields)
  // We check the ISO string to see if time is 00:00:00
  const isMidnightGMT =
    dateString.endsWith('T00:00:00Z') ||
    dateString.endsWith('T00:00:00') ||
    (dateString.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dateString));

  if (isMidnightGMT) {
    // For midnight GMT, we treat it as a date-only field and don't shift timezones
    return date.toLocaleDateString('en', {
      timeZone: 'UTC',
      dateStyle: 'medium',
    });
  }

  // Create a formatter for just checking the time part in the target timezone
  const timeFormatter = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });

  // Get the time string in the agent's timezone
  const timeString = timeFormatter.format(date);

  // Check if the time is midnight (00:00:00 or 00:00) in the target timezone
  const isMidnightLocal = timeString === '00:00:00' || timeString === '00:00';

  // Format the full date based on whether it's midnight
  return date.toLocaleString('en', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: isMidnightLocal ? undefined : 'short',
  });
};

/**
 * Formats a date for a section header (e.g. "Today", "Yesterday", "Jan 1, 2024")
 */
export const formatDateHeader = (
  dateString: string,
  timezone: string,
): string => {
  // Check if the string already has a timezone indicator
  const hasTimezone = /(?:[Zz]|[+-]\d{2}(:?\d{2})?)$/.test(dateString);
  const parsedDateString = hasTimezone ? dateString : `${dateString}Z`;
  const date = new Date(parsedDateString);
  const now = new Date();

  // Create dates in the target timezone for comparison
  // We use "en-CA" (YYYY-MM-DD) to easily compare dates without time
  const targetDateStr = date.toLocaleDateString('en-CA', {
    timeZone: timezone,
  });
  const todayStr = now.toLocaleDateString('en-CA', { timeZone: timezone });

  // Calculate yesterday's date in target timezone
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString('en-CA', {
    timeZone: timezone,
  });

  if (targetDateStr === todayStr) {
    return 'Today';
  } else if (targetDateStr === yesterdayStr) {
    return 'Yesterday';
  }
  // Return standard date format (e.g. "Oct 24, 2023")
  return date.toLocaleDateString('en', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year:
      new Date(targetDateStr).getFullYear() === new Date(todayStr).getFullYear()
        ? undefined
        : 'numeric',
  });
};

/**
 * Formats just the time for a message (e.g. "10:23 AM")
 */
export const formatTimeForMessage = (
  dateString: string,
  timezone: string,
): string => {
  // Check if the string already has a timezone indicator
  const hasTimezone = /(?:[Zz]|[+-]\d{2}(:?\d{2})?)$/.test(dateString);
  const parsedDateString = hasTimezone ? dateString : `${dateString}Z`;
  const date = new Date(parsedDateString);
  return date.toLocaleTimeString('en', {
    timeZone: timezone,
    timeStyle: 'short',
  });
};

/**
 * Helper function to determine if a string contains HTML
 */
export const isHTML = (str: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(str);
};

/**
 * Helper function to safely render HTML or plain text
 */
export const renderContent = (content: string | object): JSX.Element => {
  if (typeof content === 'object') {
    return <pre>{JSON.stringify(content, null, 2)}</pre>;
  }

  if (typeof content === 'string' && isHTML(content)) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: sanitizeHTML(content),
        }}
      />
    );
  }

  return <span>{content as string}</span>;
};

/**
 * Get the agent's timezone or fallback to client timezone or UTC
 */
export const getAgentTimezone = (
  agent?: Agent,
  clientConfig?: ClientConfig | null,
): string => {
  // First try the agent's timezone
  if (agent?.timezone) {
    return agent.timezone;
  }
  // Fallback to client config timezone
  if (clientConfig?.timezone) {
    return clientConfig.timezone;
  }
  // Default fallback
  return 'America/New_York';
};

/**
 * Fetches client configuration from the API - enhanced to work with both ID types
 */
export const fetchClientConfig = async (
  clientId: string | undefined,
  accessToken: string | undefined,
  apiUrl: string,
): Promise<ClientConfig | null> => {
  if (!clientId || !accessToken) {
    console.error('Missing clientId or accessToken for fetching client config');
    return null;
  }

  try {
    const url = `${apiUrl}/api/configs/${clientId}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch client config: ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    console.error('An error occurred while fetching client config:', err);
    return null;
  }
};
